/**
 * La partie en ligne, à l'écran.
 *
 * Le contraste avec le solo tient en une phrase : **ici, l'écran ne décide de rien**.
 * Il envoie des demandes au serveur et affiche ce qu'on lui renvoie. C'est pour cela
 * qu'il n'y a pas de `agir()` ici, pas de tireur, pas de pilote d'IA — le serveur
 * tire, arbitre et fait jouer les adversaires.
 *
 * Ce qui reste identique, en revanche, est tout le reste : la scène, `tableau.ts`,
 * `desAffiches`. L'état projeté par le serveur redevient une `Partie` ordinaire
 * (`reconstruire`), donc l'affichage ne connaît rien du réseau.
 */

import { useCallback, useState } from "react";

import { Scene } from "../scene/Scene.js";
import { Annonces, tonCourant, useAnnonces } from "../jeu/BandeauAnnonces.js";
import type { DePose, Jet } from "../scene/lancer.js";
import { posesApresJet, tapisInitial } from "../scene/tapis.js";
import { actionsOffertes, desAffiches, garderOuRelacher } from "../jeu/session.js";
import {
  consigne, enjeuDeLaPhase, lignesJoueurs, podium, posesTriees,
} from "../jeu/tableau.js";
import { useTextes } from "../i18n/contexte.js";
import { nomDeLEtapeReseau } from "../jeu/libelles.js";
import { useBruitage } from "../son/useBruitage.js";
import type { EnLigne } from "./connexion.js";

interface Props {
  readonly enLigne: EnLigne;
  /** Le son et l'haptique, réglés à l'accueil et retenus par le coffre. */
  readonly son: boolean;
  readonly onSon: (actif: boolean) => void;
}

export function PartieEnLigne({ enLigne, son, onSon }: Props) {
  const [poses, setPoses] = useState<readonly DePose[]>(
    () => tapisInitial().map((d) => d.pose));
  /** Incrémenté à chaque jet posé : c'est ce qui déclenche le regard sur le résultat. */
  const [regard, setRegard] = useState(0);

  const textes = useTextes();
  const session = enLigne.etat.session;
  const monSiege = enLigne.monSiege ?? undefined;

  // Le bruitage se déduit des annonces, qui se déduisent de l'état : c'est
  // exactement ce qui le rend utilisable ici sans une ligne de plus côté réseau
  // (§ 6.4). Un son bâti sur les événements du moteur, lui, se serait perdu.
  const bruitage = useBruitage(session.partie, monSiege, son);

  const surJetJoue = useCallback((indices: readonly number[], jet: Jet) => {
    setPoses((precedentes) => posesApresJet(precedentes, indices, jet));
    setRegard((n) => n + 1);
    enLigne.jetJoue();
  }, [enLigne]);

  const surDeClique = useCallback((index: number) => {
    const action = garderOuRelacher(session, index, monSiege);
    if (!action) return;
    bruitage.surGarde(!(session.partie.manche?.enCours?.des[index]?.garde ?? false));
    enLigne.jouer(action);
  }, [session, monSiege, enLigne, bruitage]);

  const annonces = useAnnonces(session.partie, monSiege);
  const offertes = actionsOffertes(session, monSiege);
  const des = desAffiches(session);
  const aRelancer = des.filter((d) => !d.garde).length;
  const fini = enLigne.phase === "TERMINEE" && session.demande === null;
  const posees = posesTriees(session.partie, textes);

  return (
    <div className="ecran">
      <Scene
        ambiance={tonCourant(annonces)}
        des={des}
        poses={poses}
        demande={session.demande}
        onJetJoue={surJetJoue}
        onDeClique={surDeClique}
        regard={regard}
        onImpact={bruitage.surChoc}
        /* Un jet dont l'animation ne finira jamais bloquerait la file : on le
           considère joué. L'état, lui, vient du serveur — rien n'est perdu. */
        onContextePerdu={enLigne.jetJoue}
      />

      <Annonces annonces={annonces} />

      {/* Le même bouton qu'en solo, au même endroit : on joue en ligne au comptoir
          aussi, et on n'y quitte pas une partie pour baisser le volume. */}
      <button
        className="son"
        aria-pressed={son}
        aria-label={son ? textes.couperLeSon : textes.remettreLeSon}
        onClick={() => onSon(!son)}
      >
        <span aria-hidden="true">{son ? "🔊" : "🔇"}</span>
      </button>

      <div className="bandeau">
        <p className="etape-cave">
          {(() => { const e = nomDeLEtapeReseau(enLigne.etape, textes);
            return e ? `${e} · ` : ""; })()}{textes.tableNumero(enLigne.code ?? "")}
        </p>
        <p className="phase">{enjeuDeLaPhase(session.partie, textes)}</p>
        <p className="masse">
          Masse&nbsp;: <strong>{session.partie.masse}</strong>
          {session.partie.manche && ` · manche ${session.partie.manche.numero}`}
          {enLigne.etat.enRetard && textes.affichageRattrape}
        </p>
        <ul className="zinc" aria-label={textes.lesJoueurs}>
          {lignesJoueurs(session.partie, textes).map((ligne) => {
            const reseau = enLigne.joueurs.find((j) => j.siege === ligne.siege);
            const absent = reseau?.presence === "ABSENT";
            const remplace = reseau?.presence === "REMPLACE_PAR_IA";
            return (
              <li
                key={ligne.siege}
                className={`joueur${ligne.aLaMain ? " joueur--main" : ""}`
                  + `${ligne.sauve || absent ? " joueur--sauve" : ""}`}
              >
                <span className="joueur-nom">
                  {ligne.nom}{ligne.siege === enLigne.monSiege ? textes.cEstToi : ""}
                </span>
                <span className="joueur-jetons">
                  {absent ? textes.joueurDeconnecte
                    : remplace ? textes.joueurRemplace
                      : ligne.sauve ? textes.sauve
                        : textes.jetons(ligne.jetons)}
                </span>
                {ligne.pose && <span className="joueur-pose">{ligne.pose.nom}</span>}
              </li>
            );
          })}
        </ul>
        {posees.length > 0 && (
          <>
            <p className="titre-poses">{textes.dejaPose}</p>
            <ol className="poses">
              {posees.map((score) => (
                <li key={score.siege}>
                  <span>{score.nom}</span>
                  <span className="poses-combinaison">{score.combinaison}</span>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>

      <div className="hud">
        <p className="etape" role="status" aria-live="polite">{consigne(session, textes)}</p>

        <ul className="des" aria-label={textes.lesTroisDes}>
          {des.map((de, i) => (
            <li key={i}>
              <button
                type="button"
                className={`de${de.garde ? " de--garde" : ""}`}
                aria-pressed={de.garde}
                disabled={!offertes.includes("GARDER") || de.valeur === null}
                onClick={() => surDeClique(i)}
              >
                <span aria-hidden="true">{de.valeur ?? "?"}</span>
                <span className="lecteur-ecran">
                  {`Dé ${i + 1} : ${de.valeur === null ? textes.deEnLAir
                    : `${de.valeur}${de.garde ? ", gardé" : ""}`}`}
                </span>
              </button>
            </li>
          ))}
        </ul>

        <div className="actions">
          <button
            className="bouton"
            onClick={() => {
              bruitage.surLancer(aRelancer);
              enLigne.jouer({ type: "LANCER" });
            }}
            disabled={!offertes.includes("LANCER")}
          >
            {session.partie.manche?.enCours
              ? `🎲 Relancer ${aRelancer} dé${aRelancer > 1 ? "s" : ""}`
              : "🎲 Lancer les dés"}
          </button>
          <button
            className="bouton bouton--discret"
            onClick={() => enLigne.jouer({ type: "ARRETER" })}
            disabled={!offertes.includes("ARRETER")}
          >
            Je m’arrête
          </button>
        </div>

        {enLigne.refus && <p className="alerte" role="alert">{enLigne.refus}</p>}
      </div>

      {fini && (
        <div className="fin" role="dialog" aria-label={textes.finDePartie}>
          <h2>{textes.cEstFini}</h2>
          <ol className="podium">
            {podium(session.partie).map((rang) => (
              <li key={rang.siege} className={rang.paieLaTournee ? "podium--dernier" : ""}>
                <span className="podium-rang">{rang.rang}</span>
                <span>{rang.nom}</span>
                {rang.paieLaTournee && <span className="podium-note">{textes.paieSaTournee}</span>}
              </li>
            ))}
          </ol>
          <p className="note">
            La soirée continue : la cave suivante démarre dans quelques secondes.
          </p>
          <button className="bouton bouton--discret" onClick={enLigne.quitter}>
            Quitter la table
          </button>
        </div>
      )}

      {!fini && <button className="quitter" onClick={enLigne.quitter}>{textes.quitter}</button>}
    </div>
  );
}
